<template>
  <div class="flex-1 p-3 sm:p-4 overflow-y-auto" ref="messageContainer">
    <div v-if="loading" class="flex justify-center py-4">
      <div class="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
    </div>

    <div v-else-if="messages.length === 0" class="flex flex-col items-center justify-center h-full text-center px-4">
      <span class="material-symbols-outlined text-4xl text-gray-300">forum</span>
      <p class="mt-2 text-gray-500">No messages yet</p>
      <p class="text-sm text-gray-400">Start the conversation.</p>
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="(message, index) in messagesWithDates"
        :key="message.id || index"
      >
        <div v-if="message.isDateDivider" class="flex justify-center my-4">
          <span class="px-4 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
            {{ message.date }}
          </span>
        </div>

        <div
          v-else
          class="flex items-end gap-2"
          :class="isOwnMessage(message) ? 'justify-end' : 'justify-start'"
        >
          <div
            v-if="!isOwnMessage(message)"
            class="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden shrink-0 mb-5"
            :title="getSenderName(message)"
          >
            <img
              v-if="canShowSenderAvatar(message)"
              :src="getSenderAvatar(message)"
              :alt="getSenderName(message)"
              class="h-full w-full object-cover"
              loading="lazy"
              @error="markSenderAvatarFailed(message)"
            />
            <span v-else class="text-xs font-semibold leading-none">
              {{ getSenderInitials(message) }}
            </span>
          </div>

          <div
            class="max-w-[78%] sm:max-w-md lg:max-w-lg px-4 py-2 rounded-lg break-words text-left"
            :class="isOwnMessage(message)
              ? 'bg-blue-600 text-white rounded-br-none'
              : 'bg-gray-100 text-gray-800 rounded-bl-none'"
          >
            <div
              v-if="showSenderNames && !isOwnMessage(message)"
              class="flex items-center gap-1.5 text-xs font-semibold text-blue-800 mb-1"
            >
              <span>{{ getSenderName(message) }}</span>
              <img
                v-if="isVerifiedSender(message)"
                :src="verifiedBadge"
                alt="Verified"
                class="h-3.5 w-3.5"
              />
            </div>
            <p class="whitespace-pre-wrap text-left">{{ message.text }}</p>
            <p
              class="text-right text-xs mt-1"
              :class="isOwnMessage(message) ? 'text-blue-200' : 'text-gray-500'"
            >
              {{ formatTime(message.timestamp) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import verifiedBadge from '@/assets/verifiedBadge.png';

export default {
  data() {
    return {
      failedAvatarKeys: {},
      verifiedBadge
    };
  },
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    currentUserId: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    showSenderNames: {
      type: Boolean,
      default: false
    },
    participants: {
      type: Array,
      default: () => []
    },
    ownedSenderIds: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    participantsById() {
      return this.participants.reduce((lookup, participant) => {
        if (participant?.id) lookup[participant.id] = participant;
        return lookup;
      }, {});
    },
    messagesWithDates() {
      if (!this.messages.length) return [];

      const result = [];
      let currentDate = null;

      this.messages.forEach(message => {
        const timestamp = this.toDate(message.timestamp);
        const messageDate = this.formatDate(timestamp);

        if (messageDate !== currentDate) {
          currentDate = messageDate;
          result.push({
            isDateDivider: true,
            date: messageDate
          });
        }

        result.push(message);
      });

      return result;
    }
  },
  methods: {
    isOwnMessage(message) {
      return message.senderId === this.currentUserId ||
        this.ownedSenderIds.includes(message.senderId);
    },
    getSender(message) {
      return this.participantsById[message.senderId] || {};
    },
    getSenderName(message) {
      return message.senderName ||
        this.participantsById[message.senderId]?.name ||
        'Student';
    },
    getSenderAvatar(message) {
      const sender = this.getSender(message);
      return this.normalizeImageUrl(sender.profilePicture || sender.photoURL || '');
    },
    isVerifiedSender(message) {
      return Boolean(this.getSender(message).verified || message.senderType === 'department');
    },
    getSenderInitials(message) {
      return this.getSenderName(message)
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .substring(0, 2)
        .toUpperCase() || '?';
    },
    getSenderAvatarKey(message) {
      return message.senderId || message.id || 'unknown';
    },
    canShowSenderAvatar(message) {
      return Boolean(this.getSenderAvatar(message)) &&
        !this.failedAvatarKeys[this.getSenderAvatarKey(message)];
    },
    markSenderAvatarFailed(message) {
      this.failedAvatarKeys = {
        ...this.failedAvatarKeys,
        [this.getSenderAvatarKey(message)]: true
      };
    },
    normalizeImageUrl(value) {
      if (typeof value !== 'string') return '';

      const imageUrl = value.trim();
      return imageUrl && !['null', 'undefined'].includes(imageUrl.toLowerCase())
        ? imageUrl
        : '';
    },
    toDate(timestamp) {
      if (!timestamp) return new Date();
      return timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    },
    formatTime(timestamp) {
      const date = this.toDate(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    formatDate(date) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      }

      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }

      return date.toLocaleDateString();
    },
    scrollToBottom() {
      if (this.$refs.messageContainer) {
        this.$refs.messageContainer.scrollTop = this.$refs.messageContainer.scrollHeight;
      }
    }
  },
  updated() {
    this.scrollToBottom();
  },
  mounted() {
    this.scrollToBottom();
  }
};
</script>

<style scoped>
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
